import { useRef, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { usePostHog } from "posthog-js/react";

import { EvotruxDivider } from "components/EvotruxDivider";
import { NewTabLink } from "components/NewTabLink";
import { PrivateShipmentChip } from "components/PrivateShipmentChip";
import { PublicShipmentShareButton } from "components/Shipment/PublicShipmentShareButton";
import { PublicShipmentShareModal } from "components/Shipment/PublicShipmentShareModal";
import { ShipmentCopyModal } from "components/ShipmentCopy/";
import { ShipmentHeaderLayout } from "components/ShipmentHeaderLayout";
import { RouteTab } from "components/ShipmentOverviewTab/components/RouteTab";
import { ShipmentQATab } from "components/ShipmentOverviewTab/components/ShipmentQATab";
import { ShipmentStatusBadge } from "components/ShipmentStatusBadge";
import { WhatAreMultiShipmentProjectsAlert } from "components/WhatAreMultiShipmentProjectsAlert";
import {
  ClientAppEvent,
  QUOTES_ELEMENT_ID,
  TOTAL_FREIGHT_COSTS_SHIPPER_BLOG_URL,
} from "constants/index";
import {
  useDisclosure,
  useRoleContext,
  useShipperShipmentPageContext,
} from "hooks";
import { useIsMobile } from "hooks/useIsMobile";
import {
  shipmentTypeSupportsPublicSharing,
  shipperHasWriteAccess,
} from "utils";

import { ShipmentDetails } from "../../../Shipment/components/ShipmentDetails";
import { EditableShipmentDetailTitle } from "../EditableShipmentDetailTitle";
import { ExportShipmentToPDFModal } from "../ExportShipmentToPDFModal";
import { ShipmentProgress } from "../ShipmentProgress";
import { ShipperQuoteTab } from "../ShipperQuoteTab";
import {
  ShipmentPrivateNotesSection,
  ShipmentTrackingLinkSection,
  ShipperShipmentOverviewChat,
} from "./components";
import { ChildShipmentProjectBookingsSection } from "./components/ChildShipmentProjectBookingsSection";
import { MultiShipmentProjectBookingsSection } from "./components/MultiShipmentProjectBookingsSection";
import { PendingOffPlatformBookingSection } from "./components/PendingOffPlatformBookingSection";
import { ShipmentCancellationSection } from "./components/ShipmentCancellationSection";
import { ShipmentStateSection } from "./components/ShipmentStateSection";
import { SideBarMenu } from "./components/SideBarMenu";
import { TotalFreightCostSection } from "./components/TotalFreightCostSection";
import { RefreshWithTextButton } from "./RefreshWithTextButton";

import type { PendingOffPlatformBooking } from "hooks";

import type { ShipmentProgressRef } from "../ShipmentProgress";

interface Props {
  shipment: ShipperCondensedShipment | undefined;
  onOpenCancelShipment: VoidFunction;
  shipmentCopyFormOpen: boolean;
  onShipmentCopyClose: VoidFunction;
  onShipmentCopyOpen: VoidFunction;
  onCreateTemplateFromShipmentOpen: VoidFunction;
  onUpdateEmailNotificationSettingsOpen: VoidFunction;
  onShareShipmentWithTeammatesOpen: VoidFunction;
  onOpenMarkBookedOffPlatform: VoidFunction;
  pendingOffPlatformBooking: PendingOffPlatformBooking | null;
}

export function ShipmentOverviewExpanded(props: Props) {
  const {
    shipment,
    onOpenCancelShipment,
    shipmentCopyFormOpen,
    onShipmentCopyClose,
    onShipmentCopyOpen,
    onCreateTemplateFromShipmentOpen,
    onUpdateEmailNotificationSettingsOpen,
    onShareShipmentWithTeammatesOpen,
    onOpenMarkBookedOffPlatform,
    pendingOffPlatformBooking,
  } = props;
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const { onRefresh } = useShipperShipmentPageContext();
  const shipmentProgressRef = useRef<ShipmentProgressRef>(null);
  const {
    isOpen: pdfPreviewOpen,
    onOpen: onOpenPdfPreview,
    onClose: onClosePdfPreview,
  } = useDisclosure();
  const {
    isOpen: shareModalOpen,
    onOpen: onOpenShareModal,
    onClose: onCloseShareModal,
  } = useDisclosure();
  const posthog = usePostHog();
  const { role } = useRoleContext();

  const handleOpenShareModal = () => {
    if (shipment) {
      posthog?.capture(ClientAppEvent.PUBLIC_SHIPMENT_SHARE_MODAL_OPENED, {
        shipment_id: shipment.info.id,
        source: "shipper",
        triggered_by_role: role?.id,
      });
    }
    onOpenShareModal();
  };

  const sectionHeaderType = "h5";

  const SECTION_SPACE = "4rem";
  const useColumnLayout = useIsMobile();
  const isTabletSize = useIsMobile({ maxWidth: 1200 });

  const isPubliclyShareable =
    !!shipment &&
    !!shipment.public_token &&
    shipmentTypeSupportsPublicSharing(shipment.info.type);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleOpenPdfPreview = () => {
    if (!shipment) return;
    onOpenPdfPreview();
  };

  const handleOpenNotifiedCarriersModal = () => {
    if (shipmentProgressRef.current) {
      shipmentProgressRef.current.openProgressDetail();
    }
  };

  return (
    <Box
      boxShadow={1}
      sx={{
        width: "100%",
        maxWidth: "100%",
        ...(isTabletSize && { overflowX: "auto" }),
      }}
    >
      <Box
        px="1.2rem"
        py="0.3rem"
        sx={{
          ...(isTabletSize && { minWidth: "800px" }),
        }}
      >
        <PendingOffPlatformBookingSection
          pendingOffPlatformBooking={pendingOffPlatformBooking}
        />
        <ShipmentHeaderLayout
          isColumnLayout={useColumnLayout}
          title={
            <Stack id="shipment-date-status" direction="column" minWidth={0}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                mb={1}
                minWidth={0}
              >
                <EditableShipmentDetailTitle shipment={shipment} />
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                flexWrap="wrap"
              >
                <ShipmentStatusBadge
                  status={shipment?.shipment_status}
                  size="small"
                />
                {shipment?.is_private && (
                  <PrivateShipmentChip
                    isPrivate={shipment?.is_private ?? false}
                    isProject={
                      shipment?.info?.type === "MULTI_SHIPMENT_PROJECT"
                    }
                    color="info"
                  />
                )}
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                flexWrap="wrap"
                mt={1}
              >
                <Tooltip title="Refresh">
                  <span>
                    <RefreshWithTextButton
                      onRefresh={onRefresh ?? (() => {})}
                    />
                  </span>
                </Tooltip>
                {isPubliclyShareable && (
                  <PublicShipmentShareButton onClick={handleOpenShareModal} />
                )}
              </Stack>
            </Stack>
          }
          progress={
            <ShipmentProgress ref={shipmentProgressRef} shipment={shipment} />
          }
          actions={
            <Stack
              id="button-stack"
              justifyContent="center"
              alignItems="flex-end"
              direction="row"
              spacing={5}
              sx={{ pr: 4 }}
            >
              <Button
                variant="outlined"
                size="large"
                startIcon={<MoreVertIcon />}
                onClick={handleDrawerOpen}
                sx={{
                  ...(drawerOpen && { display: "none" }),
                  textTransform: "none",
                  borderRadius: "8px",
                  py: 1,
                  px: 2,
                  color: "black",
                  borderColor: "black",
                  "&:hover": {
                    borderColor: "black",
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                }}
              >
                More
              </Button>
            </Stack>
          }
        />
        <SideBarMenu
          open={drawerOpen}
          handleDrawerClose={handleDrawerClose}
          onShareShipmentWithTeammatesOpen={onShareShipmentWithTeammatesOpen}
          onShipmentCopyOpen={onShipmentCopyOpen}
          onOpenCancelShipment={onOpenCancelShipment}
          onCreateTemplateFromShipmentOpen={onCreateTemplateFromShipmentOpen}
          onUpdateEmailNotificationSettingsOpen={
            onUpdateEmailNotificationSettingsOpen
          }
          onOpenPdfPreview={handleOpenPdfPreview}
          onOpenNotifiedCarriersModal={handleOpenNotifiedCarriersModal}
          onOpenMarkBookedOffPlatform={onOpenMarkBookedOffPlatform}
        />
        <ShipmentCancellationSection
          dividerComponent={
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant={sectionHeaderType}>
                  Cancellation Reason
                </Typography>
                <Tooltip
                  title={
                    <Typography variant="h5">
                      The reason provided by the shipper when cancelling the
                      shipment.
                    </Typography>
                  }
                >
                  <InfoOutlinedIcon color="primary" fontSize="large" />
                </Tooltip>
              </Stack>

              <EvotruxDivider />
            </>
          }
        />
        <TotalFreightCostSection
          dividerComponent={
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant={sectionHeaderType}>
                  Total Freight Costs
                </Typography>
                <NewTabLink href={TOTAL_FREIGHT_COSTS_SHIPPER_BLOG_URL}>
                  Learn more
                </NewTabLink>
              </Stack>
              <EvotruxDivider />
            </>
          }
        />
        <ShipmentStateSection
          dividerComponent={
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant={sectionHeaderType}>
                  Shipment Status
                </Typography>
                <Tooltip
                  title={
                    <Typography variant="h5">
                      View your booked shipment&apos;s status here.
                    </Typography>
                  }
                >
                  <InfoOutlinedIcon color="primary" fontSize="large" />
                </Tooltip>
              </Stack>
              <EvotruxDivider />
            </>
          }
        />
        <ShipmentTrackingLinkSection
          dividerComponent={
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant={sectionHeaderType}>
                  Shipment Tracking
                </Typography>
                <Tooltip
                  title={
                    <Typography variant="h5">
                      Manage your booked shipment&apos;s tracking here.
                    </Typography>
                  }
                >
                  <InfoOutlinedIcon color="primary" fontSize="large" />
                </Tooltip>
              </Stack>
              <EvotruxDivider />
            </>
          }
        />
        {shipment &&
          (shipment?.shipment_status === "BOOKED" ||
            shipment?.shipment_status === "BOOKING_CANCELLED" ||
            shipment?.shipment_status === "PICKED_UP" ||
            shipment?.shipment_status === "DELIVERED") &&
          shipment?.info.type !== "MULTI_SHIPMENT_PROJECT" && (
            <ShipperShipmentOverviewChat
              key={shipment.info.id}
              dividerComponent={
                <>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant={sectionHeaderType}>
                      Live Chat
                    </Typography>
                    <Tooltip
                      title={
                        <Typography variant="h5">
                          Speak to the carrier in real time here.
                        </Typography>
                      }
                    >
                      <InfoOutlinedIcon color="primary" fontSize="large" />
                    </Tooltip>
                  </Stack>
                  <EvotruxDivider />
                </>
              }
            />
          )}
        <MultiShipmentProjectBookingsSection
          dividerComponent={
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant={sectionHeaderType}>
                  Project Bookings
                </Typography>
                <Tooltip
                  title={
                    <Typography variant="h5">
                      Shipments booked for your project will collect here.
                    </Typography>
                  }
                >
                  <InfoOutlinedIcon color="primary" fontSize="large" />
                </Tooltip>
              </Stack>
              <EvotruxDivider />
            </>
          }
          parentShipmentId={shipment?.project?.project}
        />
        <ChildShipmentProjectBookingsSection
          dividerComponent={
            <>
              <Stack direction="row" alignItems="center" spacing={1} mt="1rem">
                <Typography variant={sectionHeaderType}>
                  Related Project Bookings
                </Typography>
                <Tooltip
                  title={
                    <Typography variant="h5">
                      The rest of bookings made for this project.
                    </Typography>
                  }
                >
                  <InfoOutlinedIcon color="primary" fontSize="large" />
                </Tooltip>
              </Stack>
              <EvotruxDivider />
            </>
          }
          parentShipmentId={shipment?.project?.project}
        />
        <Stack
          id={QUOTES_ELEMENT_ID}
          direction="row"
          mt="1rem"
          alignItems="center"
          spacing={1}
        >
          <Typography variant={sectionHeaderType}>Quotes</Typography>
        </Stack>
        <EvotruxDivider />
        <Box
          mt="1rem"
          sx={{
            width: "100%",
            maxWidth: "100%",
            position: "relative",
          }}
        >
          <ShipperQuoteTab shipment={shipment} />
        </Box>

        <Stack
          direction="row"
          mt={SECTION_SPACE}
          alignItems="center"
          spacing={1}
        >
          <Typography variant={sectionHeaderType}>
            Questions & Answers
          </Typography>
          <Tooltip
            title={
              <Typography variant="h5">
                Carriers can ask questions about your load here and you can
                answer them. All questions and answers are publicly visible. Do
                not discuss rates or send contact information here.
              </Typography>
            }
          >
            <InfoOutlinedIcon color="primary" fontSize="large" />
          </Tooltip>
        </Stack>
        <EvotruxDivider />

        <ShipmentQATab shipment={shipment} />

        <Stack
          direction="row"
          mt={SECTION_SPACE}
          alignItems="center"
          spacing={1}
        >
          <Typography variant={sectionHeaderType}>Shipment Details</Typography>
        </Stack>
        <EvotruxDivider />
        <WhatAreMultiShipmentProjectsAlert shipment={shipment} />
        <ShipmentDetails shipment={shipment} />

        <Stack
          direction="row"
          mt={SECTION_SPACE}
          alignItems="center"
          spacing={1}
        >
          <Typography variant={sectionHeaderType}>Route</Typography>
        </Stack>
        <EvotruxDivider />

        <RouteTab
          shipment={shipment}
          canDeleteSuggestedRoute={
            shipment != null && shipperHasWriteAccess(shipment.access_type)
          }
        />

        <ShipmentPrivateNotesSection
          dividerComponent={
            <>
              <Stack
                direction="row"
                mt={SECTION_SPACE}
                alignItems="center"
                spacing={1}
              >
                <Typography variant={sectionHeaderType}>
                  Private Notes
                </Typography>
                <Tooltip
                  title={
                    <Typography variant="h5">
                      Add private notes to this shipment. These notes are only
                      visible to you and your teammates with access to this
                      shipment.
                    </Typography>
                  }
                >
                  <InfoOutlinedIcon color="primary" fontSize="large" />
                </Tooltip>
              </Stack>
              <EvotruxDivider />
            </>
          }
        />
      </Box>

      {shipmentCopyFormOpen && (
        <ShipmentCopyModal
          onShipmentCopyClose={onShipmentCopyClose}
          shipment={shipment}
        />
      )}

      {shipment && (
        <ExportShipmentToPDFModal
          open={pdfPreviewOpen}
          onClose={onClosePdfPreview}
          shipment={shipment}
        />
      )}

      {isPubliclyShareable && shipment?.public_token && (
        <PublicShipmentShareModal
          open={shareModalOpen}
          onClose={onCloseShareModal}
          publicToken={shipment.public_token}
          shipmentId={shipment.info.id}
          source="shipper"
        />
      )}
    </Box>
  );
}
